/**
 * `/app/account/social-channels` — where an accepted connection can find this member (`MEM-008`).
 *
 * ## The prototype's Connect/Disconnect is not what this is
 *
 * `Member App.dc.html`'s SOCIAL CHANNELS SETTINGS draws four rows each with a **Connect** or
 * **Disconnect** button, which reads as OAuth: press Connect, get bounced to LinkedIn, come back
 * linked. `MEM-008` AC-4 specifies something else — *"a channel accepts a URL or a handle and is
 * normalised on save, as the organizer website field is"* — and that is what is built: four text
 * fields, one Save, and normalisation on the server.
 *
 * The story wins because the prototype's control would be a promise the product cannot keep. There
 * is no OAuth app for X or Instagram in this platform, `MEM-003` collects LinkedIn as a typed
 * string at registration, and the connection detail already renders it as a link. A Connect button
 * that opened nothing would be worse than a field. Recorded on the PR as a deliberate divergence.
 *
 * ## Why this screen matters more than it looks
 *
 * These are how a connection reaches this member **off** H2HSpark, which is where a conversation
 * usually goes once it is real. The read side shipped with the connections slice — the connection
 * detail's *Elsewhere* card — and the write side was `MEM-008`, unbuilt, which meant a member's
 * channels were whatever registration captured and could never be changed. `MEM-008`'s own note
 * asks for the consequence to be said on screen, and
 * {@link MEMBER_SOCIAL_CHANNELS_COPY.emptyWarning} is it.
 *
 * **This paragraph used to say these were the only way two connected members reach each other,
 * citing ADR 032.** ADR 052 supersedes it and `MEM-054` built the thread, so that is false and
 * the copy was already reworded for it — see the constant's own note. The header is corrected in
 * the same sweep, because a screen whose copy tells the truth over a header that does not is how
 * the copy gets "fixed" back.
 *
 * ## What the 10 September prototype does give this screen
 *
 * `Web App.dc.html`'s `showChannels` (lines 897–908) is drawn here in full apart from the control
 * type argued above: the back link, the 4xl title over a 600px lede, one 560px card of four
 * labelled rows, each label a 28px branded tile beside the channel's name, and a full-width pill
 * Save at the foot of the card. The **fields stay fields** — that is AC-4, and the only thing on
 * this screen the prototype does not get.
 */
import {
  Button,
  Card,
  CardContent,
  Field,
  Input,
  Skeleton,
  cn,
  useErrorToast,
} from '@h2hspark/ui';
import { Globe, Instagram, Linkedin, Twitter, type LucideIcon } from 'lucide-react';
import * as React from 'react';

import { PageBackLink } from '@/member/page-back-link';

import { useSaveSocialChannels, useSocialChannels } from './api';
import {
  MEMBER_ACCOUNT_COPY,
  MEMBER_PROFILE_ROUTES,
  MEMBER_SOCIAL_CHANNELS_COPY,
} from './constants';

/**
 * The prototype's per-channel tile — a 28px rounded square in the channel's own colour with a
 * white glyph, which is the only thing that tells the four rows apart at a glance.
 *
 * **Presentation only, and keyed rather than branched.** The channels themselves still arrive from
 * the server (`channels.data.channels`), so a channel this map has never heard of renders with the
 * generic mark instead of disappearing — the list is the contract's to change, and a missing icon
 * must never be a missing field.
 */
const CHANNEL_CHROME: Record<string, { readonly Icon: LucideIcon; readonly tile: string }> = {
  linkedin: { Icon: Linkedin, tile: 'bg-[#0A66C2]' },
  x: { Icon: Twitter, tile: 'bg-[#111111]' },
  instagram: { Icon: Instagram, tile: 'bg-[#C20078]' },
  website: { Icon: Globe, tile: 'bg-primary' },
};

const GENERIC_CHROME = { Icon: Globe, tile: 'bg-primary' } as const;

export function SocialChannelsPage(): React.ReactElement {
  const channels = useSocialChannels();
  const save = useSaveSocialChannels();
  const showError = useErrorToast();

  const [draft, setDraft] = React.useState<Record<string, string> | null>(null);
  const [saved, setSaved] = React.useState(false);

  React.useEffect(() => {
    if (draft !== null || channels.data === undefined) return;
    const seeded: Record<string, string> = {};
    for (const channel of channels.data.channels) {
      seeded[channel.id] = channels.data.current[channel.id] ?? '';
    }
    setDraft(seeded);
  }, [draft, channels.data]);

  React.useEffect(() => {
    if (channels.error !== null) showError(channels.error, 'We could not load your channels.');
  }, [channels.error, showError]);

  if (channels.isLoading || draft === null || channels.data === undefined) {
    return <Skeleton className="h-80 w-full" />;
  }

  const noneRecorded = Object.values(draft).every((value) => value.trim() === '');

  return (
    <form
      className="pb-8"
      onSubmit={(event) => {
        event.preventDefault();
        // Every channel on every save. A cleared field arrives as `null` and is erased, which is
        // AC-3's "independently removable" falling out of the same path rather than a second one.
        const body: Record<string, string | null> = {};
        for (const [id, value] of Object.entries(draft)) {
          body[id] = value.trim() === '' ? null : value.trim();
        }
        save.mutate(body, { onSuccess: () => setSaved(true) });
      }}
    >
      <PageBackLink to={MEMBER_PROFILE_ROUTES.account} className="mb-[18px]">
        {MEMBER_ACCOUNT_COPY.title}
      </PageBackLink>

      <header className="mb-7">
        <h1 className="mb-1.5 text-2xl tracking-tight md:text-4xl">
          {MEMBER_SOCIAL_CHANNELS_COPY.title}
        </h1>
        <p className="max-w-[600px] text-md text-muted-foreground md:text-lg">
          {MEMBER_SOCIAL_CHANNELS_COPY.blurb}
        </p>
      </header>

      {noneRecorded ? (
        <p className="mb-5 max-w-[560px] rounded-md border border-warning/40 bg-warning-subtle px-3 py-2.5 text-sm text-foreground">
          {MEMBER_SOCIAL_CHANNELS_COPY.emptyWarning}
        </p>
      ) : null}

      <Card className="max-w-[560px]">
        <CardContent className="space-y-3 p-[26px]">
          {channels.data.channels.map((channel) => {
            const { Icon, tile } = CHANNEL_CHROME[channel.id] ?? GENERIC_CHROME;
            return (
              <Field
                key={channel.id}
                label={
                  <span className="flex items-center gap-[9px]">
                    <span
                      aria-hidden
                      className={cn(
                        'inline-flex h-7 w-7 flex-none items-center justify-center rounded-sm text-white',
                        tile,
                      )}
                    >
                      <Icon className="h-[15px] w-[15px]" />
                    </span>
                    <span className="font-bold">{channel.label}</span>
                  </span>
                }
                htmlFor={`channel-${channel.id}`}
                hint={channel.hint}
              >
                <Input
                  id={`channel-${channel.id}`}
                  className="h-11 border-[1.5px] bg-ivory-100 px-[13px] shadow-none"
                  value={draft[channel.id] ?? ''}
                  placeholder={channel.placeholder}
                  inputMode="url"
                  onChange={(event) => {
                    setSaved(false);
                    setDraft((current) =>
                      current === null ? current : { ...current, [channel.id]: event.target.value },
                    );
                  }}
                />
              </Field>
            );
          })}

          {/* The prototype's Save is the card's own last row, full width. The two notices sit
              under it rather than beside it, because at 560px there is no beside. */}
          <div className="space-y-2 pt-1">
            <Button
              type="submit"
              disabled={save.isPending}
              className="h-auto w-full rounded-pill py-[11px] font-brand text-sm font-bold"
            >
              {save.isPending ? 'Saving…' : MEMBER_SOCIAL_CHANNELS_COPY.save}
            </Button>
            {saved ? (
              <span role="status" className="block text-sm text-success">
                {MEMBER_SOCIAL_CHANNELS_COPY.saved}
              </span>
            ) : null}
            {save.error !== null ? (
              <span role="alert" className="block text-sm text-destructive">
                That did not save. Try again.
              </span>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </form>
  );
}
